import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, KeyboardEvent, PointerEvent, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
    applyResizeWidth,
    cancelResize,
    commitResize,
    endResizeSession,
    measureResizeRange,
    type ResizeRange,
    type ResizeSession,
    startResizeSession,
} from './resize-session';
import { useTileResizeContext, useTilingEngineContext } from './TilingEngineContext';
import { tileWidthVar } from './tile-resize';
import { useHoverDwell } from './use-hover-dwell';

const KEYBOARD_STEP_PX = 16;
const KEYBOARD_LARGE_STEP_PX = 64;

type DragState = {
    frameId: number | undefined;
    pendingX: number;
    readonly pointerId: number;
    readonly session: ResizeSession;
    readonly startX: number;
};

export const TileResizeHandle: FunctionComponent<{
    readonly boxRef: RefObject<HTMLElement | null>;
    readonly resizeKey: string;
}> = ({ boxRef, resizeKey }) => {
    const { containerRef } = useTilingEngineContext();
    const { handleLabel, setResizedWidth } = useTileResizeContext();
    const [isDragging, setIsDragging] = useState(false);
    const [range, setRange] = useState<ResizeRange>();
    const dragRef = useRef<DragState | null>(null);
    const hoverDwell = useHoverDwell();
    const varName = tileWidthVar(resizeKey);

    const startSession = useCallback((): ResizeSession | undefined => {
        const box = boxRef.current;
        const container = containerRef.current;

        if (is.nullOrUndefined(box) || is.null(container) || !is.null(dragRef.current)) {
            return undefined;
        }

        return startResizeSession(container, box, varName);
    }, [boxRef, containerRef, varName]);

    // Screen readers read the range on focus, so it is measured then and after every width change.
    const measureValue = useCallback((): void => {
        const session = startSession();

        if (!is.undefined(session)) {
            setRange(measureResizeRange(session));
        }
    }, [startSession]);

    const endDrag = useCallback(
        (shouldCommit: boolean): void => {
            const drag = dragRef.current;

            if (is.null(drag)) {
                return;
            }

            dragRef.current = null;
            setIsDragging(false);

            if (!is.undefined(drag.frameId)) {
                cancelAnimationFrame(drag.frameId);
            }

            if (!shouldCommit || drag.pendingX === drag.startX) {
                cancelResize(drag.session);
                return;
            }

            const desiredWidth = drag.session.startWidth + drag.startX - drag.pendingX;
            setResizedWidth(resizeKey, commitResize(drag.session, desiredWidth));
            measureValue();
        },
        [measureValue, resizeKey, setResizedWidth],
    );

    // Escape abandons a drag before anything else reacts to it, e.g. a tile closing on Escape.
    useEffect(() => {
        if (!isDragging) {
            return;
        }

        const handleKeyDown = (event: globalThis.KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                endDrag(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [endDrag, isDragging]);

    useEffect(() => () => endDrag(false), [endDrag]);

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>): void => {
        if (event.button !== 0) {
            return;
        }

        const session = startSession();

        if (is.undefined(session)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        hoverDwell.reveal();
        dragRef.current = {
            frameId: undefined,
            pendingX: event.clientX,
            pointerId: event.pointerId,
            session,
            startX: event.clientX,
        };
        setIsDragging(true);
    };

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>): void => {
        const drag = dragRef.current;

        if (is.null(drag)) {
            hoverDwell.onPointerMove();
            return;
        }

        if (event.pointerId !== drag.pointerId) {
            return;
        }

        drag.pendingX = event.clientX;

        if (!is.undefined(drag.frameId)) {
            return;
        }

        drag.frameId = requestAnimationFrame(() => {
            drag.frameId = undefined;
            applyResizeWidth(drag.session, drag.session.startWidth + drag.startX - drag.pendingX);
        });
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        const step = event.shiftKey ? KEYBOARD_LARGE_STEP_PX : KEYBOARD_STEP_PX;
        const delta = event.key === 'ArrowLeft' ? step : event.key === 'ArrowRight' ? -step : undefined;

        if (event.key !== 'Home' && is.undefined(delta)) {
            return;
        }

        const session = startSession();

        if (is.undefined(session)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (is.undefined(delta)) {
            applyResizeWidth(session, undefined);
            endResizeSession(session);
            setResizedWidth(resizeKey, undefined);
        } else {
            setResizedWidth(resizeKey, commitResize(session, session.startWidth + delta));
        }

        measureValue();
    };

    const handleDoubleClick = (): void => {
        const session = startSession();

        if (is.undefined(session)) {
            return;
        }

        applyResizeWidth(session, undefined);
        endResizeSession(session);
        setResizedWidth(resizeKey, undefined);
        measureValue();
    };

    return (
        // A focusable separator is the WAI-ARIA window splitter pattern; arrows resize, Home resets.
        // biome-ignore lint/a11y/useSemanticElements: <hr> cannot take focus or pointer input
        <div
            aria-label={handleLabel}
            aria-orientation="vertical"
            aria-valuemax={range?.max}
            aria-valuemin={range?.min}
            aria-valuenow={range?.now}
            className="group/resize pointer-events-auto absolute inset-y-0 -left-m flex w-m cursor-col-resize touch-none justify-center outline-none pointer-coarse:hidden"
            data-tile-resize-handle={resizeKey}
            role="separator"
            tabIndex={0}
            onDoubleClick={handleDoubleClick}
            onFocus={measureValue}
            onKeyDown={handleKeyDown}
            onLostPointerCapture={() => endDrag(true)}
            onPointerCancel={() => endDrag(false)}
            onPointerDown={handlePointerDown}
            onPointerEnter={hoverDwell.onPointerMove}
            onPointerLeave={hoverDwell.onPointerLeave}
            onPointerMove={handlePointerMove}
            onPointerUp={() => endDrag(true)}
        >
            <div
                className={clsx(
                    'h-full w-0.5 rounded-full',
                    isDragging
                        ? 'bg-border-focus'
                        : [
                              'bg-border-strong group-focus-visible/resize:bg-border-focus group-focus-visible/resize:opacity-100',
                              hoverDwell.isRevealed
                                  ? 'opacity-100 transition-opacity duration-200 ease-enter motion-reduce:transition-none'
                                  : 'opacity-0',
                          ],
                )}
            />
        </div>
    );
};
